import { useCallback } from "react";
import { useLocation } from "react-router-dom";
import {
  resolveDesignToggleRedirectTarget,
} from "@/lib/designPreview";
import { useDesignPreference } from "@/features/design/DesignPreferenceProvider";
import { classNames } from "@/lib/format";

const DESIGN_TOGGLE_LABEL = "Neues Design";

type NewDesignToggleProps = {
  className?: string;
  /** `menu` = dezent im Profil-Dropdown. */
  variant?: "header" | "drawer" | "menu";
};

/** Schalter für das neue React-Design – Persistenz serverseitig pro Benutzer. */
export function NewDesignToggle({ className, variant = "header" }: NewDesignToggleProps) {
  const location = useLocation();
  const { newDesign: enabled, setNewDesign } = useDesignPreference();

  const handleChange = useCallback(
    (next: boolean) => {
      setNewDesign(next);
      const target = resolveDesignToggleRedirectTarget(
        next,
        location.pathname,
        location.search,
        location.hash,
      );
      if (target) {
        window.location.assign(target);
      }
    },
    [location.hash, location.pathname, location.search, setNewDesign],
  );

  if (variant === "menu") {
    return (
      <section className={classNames("eh-account-section", className)}>
        <label className="flex cursor-pointer select-none items-center justify-between gap-3">
          <span className="min-w-0">
            <span className="eh-account-section-title mb-0 block">{DESIGN_TOGGLE_LABEL}</span>
            <span className="mt-0.5 block text-xs text-label-tertiary">
              Zwischen klassischer und neuer Oberfläche wechseln
            </span>
          </span>
          <span className="relative inline-flex h-5 w-9 shrink-0 items-center">
            <input
              type="checkbox"
              className="peer sr-only"
              checked={enabled}
              onChange={(event) => handleChange(event.target.checked)}
              aria-label={DESIGN_TOGGLE_LABEL}
            />
            <span
              className={classNames(
                "absolute inset-0 rounded-full transition",
                "bg-fill peer-checked:bg-accent",
              )}
              aria-hidden="true"
            />
            <span
              className={classNames(
                "absolute left-0.5 h-4 w-4 rounded-full bg-white shadow-sm transition",
                "peer-checked:translate-x-4",
              )}
              aria-hidden="true"
            />
          </span>
        </label>
      </section>
    );
  }

  return (
    <label
      className={classNames(
        "inline-flex cursor-pointer select-none items-center gap-2 rounded-full border border-separator bg-card px-2.5 py-1.5 text-xs font-medium text-label transition hover:bg-fill-tertiary",
        variant === "drawer" && "w-full justify-between rounded-xl px-4 py-3 text-sm",
        className,
      )}
      title={DESIGN_TOGGLE_LABEL}
    >
      <span className="text-label-secondary">{DESIGN_TOGGLE_LABEL}</span>
      <span className="relative inline-flex h-5 w-9 shrink-0 items-center">
        <input
          type="checkbox"
          className="peer sr-only"
          checked={enabled}
          onChange={(event) => handleChange(event.target.checked)}
          aria-label={DESIGN_TOGGLE_LABEL}
        />
        <span
          className={classNames(
            "absolute inset-0 rounded-full transition",
            "bg-fill peer-checked:bg-accent",
          )}
          aria-hidden="true"
        />
        <span
          className={classNames(
            "absolute left-0.5 h-4 w-4 rounded-full bg-white shadow-sm transition",
            "peer-checked:translate-x-4",
          )}
          aria-hidden="true"
        />
      </span>
    </label>
  );
}
